import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  where,
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import { clientPayments } from '../../utils/clientPayments';
import {
  ADMIN_TRADE_LOGS_QUERY_LIMIT,
  adminTradeHistoryStartDate,
} from '../../utils/adminTradeHistoryWindow';

const toDate = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatDate = (value) => {
  const d = toDate(value);
  if (!d) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
};

const formatMoneyExact = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
};

const normalizeSubStatus = (raw) => {
  const s = String(raw || '').toLowerCase();
  if (!s) return 'active';
  if (s.includes('trial')) return 'trialing';
  if (s.includes('cancel') || s.includes('inactive') || s.includes('expired')) return 'cancelled';
  if (s.includes('active') || s.includes('succeed') || s.includes('paid') || s === 'complete') return 'active';
  return s;
};

const isAnnualInterval = (raw) => {
  const s = String(raw || '').toLowerCase();
  return s.includes('year') || s.includes('annual') || s === 'yr' || s === 'y';
};

const paymentAmount = (data) => {
  const raw = data.amount ?? data.subs_amount ?? data.metadata?.amount ?? 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
};

const paymentCreatedAt = (data) =>
  toDate(data.created_at) ||
  toDate(data.createdAt) ||
  toDate(data.subs_start_date) ||
  null;

/** Desk products that should not appear in admin subscriber KPIs / table. */
const isExcludedAdminProduct = (productLabel) => {
  const s = String(productLabel || '')
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!s) return false;
  if (s.includes('monthly maintenance fee') || s === 'maintenance fee') return true;
  if (s === 'testsubs' || s === 'test subs' || s.includes('test subs')) return true;
  return false;
};

const TRADES_OPERATOR_REQUIRED =
  'Trade logs need the qfaaOperator claim (email allowlist alone is not enough). Run scripts/grant-qfaa-operator.mjs, then sign out and back in.';

/**
 * Live admin datasets from Firestore (users, payments, order backlogs).
 *
 * Users + payments are readable by any signed-in account under current rules.
 * Order backlogs require the qfaaOperator custom claim for collection-wide reads
 * (allowlisted QA emails can open the shell but cannot sweep this ledger).
 *
 * @param {boolean} enabled
 * @param {{ canReadAllTrades?: boolean }} [options]
 */
export const useAdminLiveData = (enabled, { canReadAllTrades = false } = {}) => {
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [paymentDocs, setPaymentDocs] = useState([]);
  const [trades, setTrades] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [paymentsLoading, setPaymentsLoading] = useState(true);
  const [tradesLoading, setTradesLoading] = useState(true);
  const [usersError, setUsersError] = useState(null);
  const [paymentsError, setPaymentsError] = useState(null);
  const [tradesError, setTradesError] = useState(null);
  const [tradesTruncated, setTradesTruncated] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  useEffect(() => {
    if (!enabled || !db) return undefined;

    setUsersLoading(true);
    const unsub = onSnapshot(
      collection(db, 'users'),
      (snapshot) => {
        const rows = snapshot.docs.map((docSnap) => {
          const data = docSnap.data() || {};
          const created = toDate(data.createdAt) || toDate(data.created_at) || toDate(data.signupAt);
          return {
            id: docSnap.id,
            email: data.email || '',
            name:
              data.fullName ||
              data.displayName ||
              data.name ||
              data.username ||
              data.email ||
              docSnap.id,
            role: data.isAdmin || data.role === 'admin' ? 'ADMIN' : (data.role || 'USER').toUpperCase(),
            status: String(data.status || 'active').toLowerCase(),
            tier: data.tier || '',
            created,
            createdLabel: formatDate(created),
          };
        });
        rows.sort((a, b) => (b.created?.getTime() || 0) - (a.created?.getTime() || 0));
        setUsers(rows);
        setUsersLoading(false);
        setUsersError(null);
        setLastUpdatedAt(new Date());
      },
      (err) => {
        console.error('Admin users listener failed:', err);
        setUsersError(err?.message || 'Failed to load users');
        setUsersLoading(false);
      }
    );
    return unsub;
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !db) return undefined;

    setPaymentsLoading(true);
    const unsub = onSnapshot(
      query(collection(db, 'tbl_stripe_payment_history'), limit(5000)),
      (snapshot) => {
        const rows = snapshot.docs.map((docSnap) => {
          const data = docSnap.data() || {};
          const email = data.email || data.customerEmail || data.customer_email || '';
          const amount = paymentAmount(data);
          const interval =
            data.subs_interval_type ||
            data.interval ||
            data.billing_interval ||
            data.metadata?.interval ||
            'monthly';
          const product =
            data.product_details ||
            data.product ||
            data.planType ||
            data.plan_type ||
            data.subs_tier ||
            'Subscription';
          const tier = data.subs_tier || data.tier || '';
          const productLabel = tier
            ? `${product}${String(product).toLowerCase().includes(String(tier).toLowerCase()) ? '' : ` · ${tier}`}`
            : String(product);
          const status = normalizeSubStatus(
            data.payment_status || data.status || data.metadata?.payment_status || data.subs_status
          );
          const renewal =
            toDate(data.subs_renewal_date) ||
            toDate(data.renewal_date) ||
            toDate(data.current_period_end);
          const created = paymentCreatedAt(data);
          const annual = isAnnualInterval(interval);
          const deposit = /deposit|down payment/i.test(`${data.subs_description || ''} ${productLabel}`);

          return {
            id: docSnap.id,
            email,
            product: productLabel,
            amount,
            lastPayment: formatMoneyExact(amount),
            period: annual ? 'annual' : 'monthly',
            annual,
            status,
            renewal,
            renewalLabel:
              status === 'cancelled'
                ? renewal
                  ? `Cancelled ${formatDate(renewal)}`
                  : 'Cancelled'
                : formatDate(renewal),
            created,
            deposit,
          };
        })
        // A one-time QFAA deposit is not a subscription; it is reported under paymentStats.
        .filter((row) => !isExcludedAdminProduct(row.product) && !row.deposit);
        rows.sort((a, b) => (b.created?.getTime() || 0) - (a.created?.getTime() || 0));
        setPayments(rows);
        setPaymentDocs(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
        setPaymentsLoading(false);
        setPaymentsError(null);
        setLastUpdatedAt(new Date());
      },
      (err) => {
        console.error('Admin payments listener failed:', err);
        setPaymentsError(err?.message || 'Failed to load payments');
        setPaymentsLoading(false);
      }
    );
    return unsub;
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !db) return undefined;

    // Unconstrained backlog reads are operator-claim-only (firestore.rules).
    // Allowlisted shell access without the claim must not hit the listener —
    // Firestore denies the whole query and spams permission errors.
    if (!canReadAllTrades) {
      setTrades([]);
      setTradesLoading(false);
      setTradesTruncated(false);
      setTradesError(TRADES_OPERATOR_REQUIRED);
      return undefined;
    }

    setTradesLoading(true);
    setTradesError(null);
    setTradesTruncated(false);

    const historyStart = adminTradeHistoryStartDate();
    const historyStartTs = Timestamp.fromDate(historyStart);

    const mapTradeRows = (snapshot) =>
      snapshot.docs.map((docSnap) => {
        const data = docSnap.data() || {};
        const when =
          toDate(data.exit?.filledAt) ||
          toDate(data.exitFilledAt) ||
          toDate(data.entry?.filledAt) ||
          toDate(data.entryFilledAt) ||
          toDate(data.updatedAt) ||
          toDate(data.createdAt) ||
          toDate(data.created_at);
        return {
          id: docSnap.id,
          userId: data.userId || '',
          accountId: data.accountId || data.account || data.account_number || '—',
          when,
        };
      });

    // Prefer a dated window (Oct 2025+) so monthly trade logs are not an arbitrary
    // sample of the latest N docs. Falls back to a higher uncapped-by-date limit if
    // the composite/single-field index is unavailable.
    const datedQuery = query(
      collection(db, 'tbl_user_order_backlogs'),
      where('createdAt', '>=', historyStartTs),
      orderBy('createdAt', 'asc'),
      limit(ADMIN_TRADE_LOGS_QUERY_LIMIT)
    );
    const fallbackQuery = query(
      collection(db, 'tbl_user_order_backlogs'),
      limit(ADMIN_TRADE_LOGS_QUERY_LIMIT)
    );

    let unsub = null;
    let usingFallback = false;

    const attach = (q, isFallback) => {
      usingFallback = isFallback;
      let serverSynced = false;
      unsub = onSnapshot(
        q,
        { includeMetadataChanges: true },
        (snapshot) => {
          // The first snapshot can come from the shared in-memory cache holding
          // only the docs other listeners have streamed so far; counting it as
          // the full ledger shows partial totals.
          if (snapshot.metadata.fromCache && !serverSynced) return;
          serverSynced = true;
          let rows = mapTradeRows(snapshot);
          if (isFallback) {
            rows = rows.filter((t) => !t.when || t.when >= historyStart);
          }
          setTrades(rows);
          setTradesTruncated(snapshot.size >= ADMIN_TRADE_LOGS_QUERY_LIMIT);
          setTradesLoading(false);
          setTradesError(null);
          setLastUpdatedAt(new Date());
        },
        (err) => {
          console.error('Admin trades listener failed:', err);
          const msg = err?.message || 'Failed to load trades';
          if (!usingFallback && /index|requires an index/i.test(msg)) {
            if (typeof unsub === 'function') unsub();
            attach(fallbackQuery, true);
            return;
          }
          setTradesError(
            /permission|insufficient/i.test(msg) ? TRADES_OPERATOR_REQUIRED : msg
          );
          setTradesLoading(false);
        }
      );
    };

    attach(datedQuery, false);
    return () => {
      if (typeof unsub === 'function') unsub();
    };
  }, [enabled, canReadAllTrades]);

  const emailByUserId = useMemo(() => {
    const map = {};
    users.forEach((u) => {
      map[u.id] = u.email || u.name || u.id;
    });
    return map;
  }, [users]);

  const subscriberStats = useMemo(() => {
    // Latest payment row per email (keeps multi-product as separate if product differs —
    // use email+product key so dashboard table matches payment records).
    const latestByKey = new Map();
    payments.forEach((p) => {
      if (!p.email) return;
      const key = `${p.email.toLowerCase()}::${p.product}`;
      const prev = latestByKey.get(key);
      if (!prev || (p.created?.getTime() || 0) > (prev.created?.getTime() || 0)) {
        latestByKey.set(key, p);
      }
    });
    const rows = Array.from(latestByKey.values());

    let active = 0;
    let monthlyActive = 0;
    let annualActive = 0;
    let cancelling = 0;
    let mrrMonthly = 0;
    let mrrAnnual = 0;

    rows.forEach((r) => {
      if (r.status === 'cancelled') {
        cancelling += 1;
        return;
      }
      if (r.status === 'active' || r.status === 'trialing') {
        active += 1;
        if (r.annual) {
          annualActive += 1;
          mrrAnnual += r.amount / 12;
        } else {
          monthlyActive += 1;
          mrrMonthly += r.amount;
        }
      }
    });

    const mrr = mrrMonthly + mrrAnnual;
    return {
      rows: rows.sort((a, b) => (b.created?.getTime() || 0) - (a.created?.getTime() || 0)),
      active,
      monthlyActive,
      annualActive,
      cancelling,
      mrr,
      mrrMonthly,
      mrrAnnual,
      arr: mrr * 12,
    };
  }, [payments]);

  /** What each client paid JTNC: QFAA deposits, monthly fees and annual / one-time plans. */
  const paymentStats = useMemo(() => {
    const byEmail = new Map();
    paymentDocs.forEach((d) => {
      const email = String(d.email || d.customerEmail || '').trim().toLowerCase();
      if (!email) return;
      if (!byEmail.has(email)) byEmail.set(email, []);
      byEmail.get(email).push(d);
    });
    const rows = [];
    byEmail.forEach((docs, email) => {
      const p = clientPayments(docs);
      if (!p.total) return;
      const deposits = p.payments.filter((x) => x.kind === 'deposit');
      rows.push({
        email,
        deposit: p.deposit,
        depositDate: deposits[0]?.date || '',
        monthly: p.monthly,
        other: p.other,
        total: p.total,
        renewalsMissing: p.renewalsMissing,
      });
    });
    rows.sort((a, b) => b.total - a.total || a.email.localeCompare(b.email));
    const sum = (k) => Math.round(rows.reduce((s, r) => s + r[k], 0) * 100) / 100;
    return {
      rows,
      deposit: sum('deposit'),
      depositClients: rows.filter((r) => r.deposit).length,
      firstDeposit: rows.map((r) => r.depositDate).filter(Boolean).sort()[0] || '',
      monthly: sum('monthly'),
      monthlyClients: rows.filter((r) => r.monthly).length,
      other: sum('other'),
      total: sum('total'),
      renewalsMissing: rows.filter((r) => r.renewalsMissing).length,
    };
  }, [paymentDocs]);

  const userStats = useMemo(() => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const active = users.filter((u) => u.status === 'active').length;
    const newUsers = users.filter((u) => u.created && u.created >= monthStart).length;
    return {
      total: users.length,
      newUsers,
      active,
    };
  }, [users]);

  const buildTradeLogsForMonth = (year, monthIndex) => {
    const start = new Date(year, monthIndex, 1);
    const end = new Date(year, monthIndex + 1, 1);
    const inMonth = trades.filter((t) => t.when && t.when >= start && t.when < end);
    const byAccount = new Map();
    const usersWithTrades = new Set();

    inMonth.forEach((t) => {
      if (t.userId) usersWithTrades.add(t.userId);
      const key = `${t.accountId}::${t.userId}`;
      const prev = byAccount.get(key) || {
        accountId: t.accountId,
        userId: t.userId,
        email: emailByUserId[t.userId] || '—',
        count: 0,
      };
      prev.count += 1;
      byAccount.set(key, prev);
    });

    const rows = Array.from(byAccount.values()).sort((a, b) => b.count - a.count);
    return {
      rows,
      totalTrades: inMonth.length,
      usersWithTrades: usersWithTrades.size,
      connectedUsers: users.length,
    };
  };

  return {
    users,
    payments,
    trades,
    usersLoading,
    paymentsLoading,
    tradesLoading,
    usersError,
    paymentsError,
    tradesError,
    tradesTruncated,
    lastUpdatedAt,
    subscriberStats,
    paymentStats,
    userStats,
    emailByUserId,
    buildTradeLogsForMonth,
    formatMoney,
    formatDate,
  };
};

export { formatMoney, formatDate, toDate };
